import {colors} from "@c10h/colors";
import {CoronavirusSection} from "@components/Coronavirus/Section";
import {useRouter} from "next/router";
import {useTranslation} from "ni18n";
import {v5Pages} from "src/components/_common/_constants";

import useMediaQuery from "../useMediaQuery";
import {getCityAndState} from "../utils/stateUtils";
import {minMaxTravelClearancePrices} from "./CovidTravelClearanceConstants";

export const useCoronavirusConstants = (): CoronavirusSection[] => {
  const i18n = useTranslation();
  const router = useRouter();
  const isMd = useMediaQuery("md");

  return [
    {
      id: "get-tested",
      title: i18n.t("Find COVID-19\ntesting near you "),
      smallTitle: i18n.t("Get Tested"),
      subtitle: i18n.t("Choose by test type, proximity or availability"),
      icon: "microscope",
      src: "/coronavirus/yellow-map-testing",
      alt: i18n.t("A static map with a marker at a Carbon Health COVID testing location."),
      href: {
        pathname: v5Pages.getCareSlug,
        query: {
          slug: "covid-19-testing",
        },
      },
      // @ts-expect-error TS2322: Type '(cityAndState: string) => void' is not assignable to type '(cityAndState?: string | undefined) => void'.
      onSearch: (cityAndState: string) => {
        const [city, state] = getCityAndState(cityAndState);
        const pathname = v5Pages.covid19TestingCenters;
        const as = [pathname, state?.replaceAll(" ", "-"), city?.replaceAll(" ", "-")]
          .compact()
          .join("/");

        router.push({pathname, query: {state, city}}, as);
      },
    },
    {
      id: "travel-safely",
      title: i18n.t("Get cleared for travel"),
      smallTitle: i18n.t("Travel Safely"),
      subtitle: i18n.t(
        "Plan for travel and book a COVID-19 test to ensure safety and peace of mind",
      ),
      icon: "plane",
      btnProps: {
        link: true,
        href: {
          pathname: v5Pages.covid19TravelClearance,
        },
        children: i18n.t("Travel Safely"),
        hoverBg: `${colors.gray800}E6`,
      },
      line1: i18n.t("{{price}} / person", {price: minMaxTravelClearancePrices}),
      line2: i18n.t("Insurance not accepted"),
      src: "/coronavirus/woman-with-mask-in-airplane",
      alt: i18n.t("A woman wearing a mask in an airplane."),
    },
    {
      id: "covid-care",
      title: i18n.t("On-demand care for patients with COVID-19"),
      smallTitle: i18n.t("Seek COVID-19 Care"),
      subtitle: i18n.t(
        "Get relief, comfort, and expert COVID-19 care from the comfort of your home today",
      ),
      icon: "virus-molecule",
      btnProps: {
        link: true,
        href: {
          pathname: v5Pages.covidPositiveCare,
        },
        children: i18n.t("Explore Care"),
        hoverBg: `${colors.gray800}E6`,
      },
      src: "/coronavirus/person-looking-at-phone",
      alt: i18n.t("A person looking at her phone."),
    },
    {
      id: "employer-solutions",
      title: i18n.t("COVID-19 solutions for employers"),
      smallTitle: isMd ? i18n.t("Workplace Readiness") : i18n.t("Enterprise Workplace Readiness"),
      subtitle: i18n.t(
        "Maintain a safe work environment and manage COVID-19 risk with testing, vaccine tracking and more",
      ),
      icon: "clipboard",
      btnProps: {
        link: true,
        href: {
          pathname: v5Pages.covidReadyCompliance,
        },
        children: i18n.t("Learn More"),
        hoverBg: `${colors.gray800}E6`,
      },
      src: "/coronavirus/people-working",
      alt: i18n.t("Two women in a bright office working together."),
    },
    {
      id: "get-vaccinated",
      title: i18n.t("Find a vaccine\nprovider near you"),
      smallTitle: i18n.t("Get Vaccinated"),
      subtitle: i18n.t("Choose by vaccine type, proximity, or availability"),
      icon: "syringe-and-vial",
      src: "/coronavirus/blue-map-vaccine",
      alt: i18n.t("A static map with a marker at a Carbon Health COVID vaccine location."),
      // @ts-expect-error TS2322: Type '(cityAndState: string) => void' is not assignable to type '(cityAndState?: string | undefined) => void'.
      onSearch: (cityAndState: string) => {
        const [city, state] = getCityAndState(cityAndState);
        const pathname = v5Pages.vaccineNearMe;
        const as = [pathname, state?.replaceAll(" ", "-"), city?.replaceAll(" ", "-")]
          .compact()
          .join("/");

        router.push({pathname, query: {state, city}}, as);
      },
    },
  ];
};
